import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'

/**
 * The glow around the current step's element.
 *
 * Four dark panels — above, below, left, right — rather than a veil with a hole: the "hole" is
 * simply the space the panels do not cover, so nothing sits on top of the element that could
 * intercept a click.
 *
 * The whole glow is `pointer-events: none`. A typical tour library blocks clicks outside the lit
 * element, to force the guided path; here that would be a defect, because the tour's own controls
 * live OUTSIDE it — they have to survive page changes — and would get blocked too. Dimming without
 * blocking keeps the app usable and the tour followable.
 */

export interface TourSpotlightProps {
  /**
   * The SELECTOR, not the element: React can remount the node on any change (switching a tab
   * redraws the panel that holds it), and an element held onto goes detached from the document —
   * its measurements collapse to zero and the glow looks like it vanished. Looking it up every
   * time keeps the glow following the live node.
   */
  selector: string | null
  /** Breathing room between the element's edge and where the shadow starts. */
  padding?: number
}

interface Rect {
  top: number
  left: number
  width: number
  height: number
  /** The target's own border-radius: a fixed-corner outline does not dress a pill-shaped button. */
  radius: string
}

/** Menus, dropdowns and tooltips open right now. */
const FLOATING = '[role="menu"], [role="listbox"], [role="tooltip"]'

const floatingOpen = (): boolean => document.querySelector(FLOATING) !== null

const DIM_LAYER = 'z-[9998]'
const RING_LAYER = 'z-[9999]'

export const TourSpotlight = ({ selector, padding = 8 }: TourSpotlightProps): ReactElement | null => {
  const [rect, setRect] = useState<Rect | null>(null)
  const [dimmed, setDimmed] = useState(true)

  useEffect(() => {
    if (!selector) {
      setRect(null)
      return undefined
    }

    const measure = (): void => {
      setDimmed(!floatingOpen())
      const node = document.querySelector<HTMLElement>(selector)
      if (!node) {
        setRect(null)
        return
      }
      const box = node.getBoundingClientRect()
      setRect({
        top: box.top - padding,
        left: box.left - padding,
        width: box.width + padding * 2,
        height: box.height + padding * 2,
        radius: getComputedStyle(node).borderRadius,
      })
    }

    measure()
    document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // The element moves and remounts: the page scrolls, the window resizes, React remounts the
    // node. The glow follows it rather than staying put or vanishing.
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)
    const mutations = new MutationObserver(measure)
    mutations.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)

    return () => {
      observer.disconnect()
      mutations.disconnect()
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [selector, padding])

  if (!rect) return null

  const panel = `fixed bg-black/50 pointer-events-none ${DIM_LAYER}`

  return (
    <>
      {dimmed ? (
        <>
          <div className={panel} style={{ top: 0, left: 0, right: 0, height: Math.max(rect.top, 0) }} />
          <div className={panel} style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }} />
          <div
            className={panel}
            style={{ top: rect.top, left: 0, width: Math.max(rect.left, 0), height: rect.height }}
          />
          <div
            className={panel}
            style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }}
          />
        </>
      ) : null}

      {/* The outline: names the target even when the shadow around it has already retreated. */}
      <div
        className={`fixed border-2 border-brass pointer-events-none transition-all duration-200 ${RING_LAYER}`}
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: rect.radius,
        }}
      />
    </>
  )
}
